/** Whether a wandering stack fights, runs, or asks to come along.
 *
 * `monster()` raises the FIGHT_OR_FLEE prompt; this file decides what the stack
 * is actually offering before the prompt goes up. It reads and rolls, and it
 * writes nothing but the `map.encounter` stream, so a caller may ask once and
 * use the answer twice only if it holds on to it - asking again is a second
 * roll and a different answer (I5).
 *
 * ## The classic rule
 *
 * The decision is the ratio of the two armies' AI value, pushed around by
 * Diplomacy and by how much of the hero's own army is the same kind of creature
 * as the stack in front of him. The classic folds those into one integer, `charisma`,
 * and compares it against the stack's disposition:
 *
 *     powerFactor  from the strength ratio, -3 (hopeless) .. 11 (seven to one)
 *     sympathy     0, 1 or 2 - the hero already fields creatures of that kind
 *     charisma     powerFactor + sympathy + Diplomacy level
 *     margin       charisma - disposition; below zero the stack fights, full stop
 *
 * Two deviations from the original, both deliberate:
 *
 * 1. The classic's disposition is authored per stack (compliant..savage) and the
 *    payload vocabulary in `objects.ts` has no key for one, so every wandering
 *    stack here is the random-map default, "aggressive" - `WANDERING_DISPOSITION`.
 * 2. The classic then decides *without a roll*: past the threshold the stack always
 *    joins. That leaves nothing for the `diplomacy` stat, which is authored in
 *    basis points (the Dread Mask is -10000), and basis points are a
 *    probability. So the margin buys a *chance* rather than a certainty, and
 *    the skill levels buy the rest of it:
 *
 *        join  500 bp, +600 per margin point, + [0, 2000, 4000, 7000] by level
 *        flee 1500 bp, +800 per margin point, + [0,  500, 1000, 1500] by level
 *
 *    A hero with Expert Diplomacy standing over a stack a third his size is at
 *    500 + 6*600 + 7000, i.e. certain - which is the reputation Expert Diplomacy
 *    has, and what the skill's third line ("join readily") promises.
 *
 * The willingness is clamped to a probability *before* the `diplomacy` stat is
 * added, so the stat is the last word: -10000 is minus certainty and no margin
 * can outrun it. That is what makes the Dread Mask's "will never offer to
 * join and always flee if they can" true rather than merely likely. The same
 * number read the other way is fear: a stack that will not treat with the
 * wearer at all would rather run, so the magnitude of a *negative* stat is
 * added to the flee chance. A positive stat does not make stacks braver; it
 * only makes them friendlier.
 *
 * ## Which way each branch can be shut
 *
 * A stack only offers to join if the hero can house it (`hasFreeSlotFor`) - an
 * army of seven full slots is the commonest reason a classic player watches an
 * offer he expected never arrive - and only flees when it is at least
 * `FLEE_MIN_RATIO_BP` outmatched, because a stack that can win has no reason to
 * run. Either branch can also be vetoed by the map author, through the payload
 * keys `objects.ts` already documents:
 *
 *     flees             true: this stack will run rather than die.
 *                       Absent or false is the classic's "never flees" - it fights.
 *     joinCostPerHead   gold per creature the stack asks for. Zero or absent
 *                       falls back to `armyGoldCost`. **Negative means the
 *                       stack never joins at all**, whatever the roll says.
 *
 * Both vetoed is a stack that never bargains: it always fights. Every monster
 * def shipped today carries `flees: false`, so nothing in the current content
 * runs away - that is a data decision, not a gap here.
 *
 * Whether the player can *afford* the price is the caller's business: this
 * returns the number, `monster()` raises the confirm that spends it.
 */

import type { ArmyId, CreatureDefId } from "../ids.js";
import type { ArmyState, HeroState } from "../state.js";
import { BP_ONE, clampInt } from "../numeric.js";
import type { Ctx } from "./ctx.js";
import { rollChanceBp } from "./ctx.js";
import type { Payload } from "./payload.js";
import { payFlag, payNum } from "./payload.js";
import {
  CODED_SKILLS, armyGoldCost, armyValue, hasFreeSlotFor, skillLevelOf, statFor,
} from "./hero.js";

// ----------------------------------------------------------------- numbers

/**
 * Every wandering stack is "aggressive", the disposition a classic random map
 * gives a monster it places itself. See the header: there is no payload key to
 * author a compliant or a savage one.
 */
export const WANDERING_DISPOSITION = 2;

export const JOIN_BASE_BP = 500;
export const JOIN_PER_MARGIN_BP = 600;
/** Willingness to join, by Diplomacy level. The skill's three lines, in order. */
export const DIPLOMACY_JOIN_BP: readonly number[] = Object.freeze([0, 2000, 4000, 7000]);

export const FLEE_BASE_BP = 1500;
export const FLEE_PER_MARGIN_BP = 800;
/** A hero who can be bargained with is also a hero worth running from. */
export const DIPLOMACY_FLEE_BP: readonly number[] = Object.freeze([0, 500, 1000, 1500]);

/**
 * A stack runs only when the hero's army is worth three times its own. Below
 * that it may still lose, but it has a fight to offer and the classic makes it take
 * it; above it, running is the only move it has left.
 */
export const FLEE_MIN_RATIO_BP = 30000;

export type DiplomacyOffer =
  | { readonly kind: "join"; readonly price: number }
  | { readonly kind: "flee" }
  | { readonly kind: "fight" };

const FIGHT: DiplomacyOffer = Object.freeze({ kind: "fight" });
const FLEE: DiplomacyOffer = Object.freeze({ kind: "flee" });

// ------------------------------------------------------------------ pieces

/**
 * The classic's strength-ratio ladder, in basis points of the ratio rather than in
 * floating point. It is deliberately not linear: everything from seven to one
 * upwards is the same overwhelming, and everything under one to three is the
 * same hopeless, so only the middle of the range is worth resolving finely.
 */
function powerFactor(ratioBp: number): number {
  if (ratioBp >= 7 * BP_ONE) return 11;
  if (ratioBp >= BP_ONE) return Math.floor((2 * (ratioBp - BP_ONE)) / BP_ONE);
  if (ratioBp >= BP_ONE / 2) return -1;
  // A third, as an integer: below this the stack is not interested in talking.
  if (ratioBp >= 3333) return -2;
  return -3;
}

/** The base of an upgrade chain, so that Archers and Master Bowyers count as one kind. */
function kindRoot(c: Ctx, def: CreatureDefId): CreatureDefId {
  let cur = def;
  // Bounded rather than while(true): a content cycle in `upgradeOf` would
  // otherwise hang the kernel, and a hang is not a returnable illegality (I5).
  for (let i = 0; i < 4; i++) {
    const d = c.content.creatures.get(cur);
    if (d === undefined || d.upgradeOf === null) return cur;
    cur = d.upgradeOf;
  }
  return cur;
}

/**
 * 0, 1 or 2, by how many of the hero's own stacks are the same kind of creature
 * as the guard. Wolves think better of a man already leading wolves; this is
 * why the same hero gets different answers from two identical stacks.
 */
function sympathy(c: Ctx, own: ArmyState | null, guard: ArmyState): number {
  if (own === null) return 0;
  const roots: CreatureDefId[] = [];
  for (const g of guard.slots) {
    if (g === null || g.count <= 0) continue;
    const r = kindRoot(c, g.def);
    if (!roots.includes(r)) roots.push(r);
  }
  let shared = 0;
  for (const s of own.slots) {
    if (s === null || s.count <= 0) continue;
    if (roots.includes(kindRoot(c, s.def))) shared += 1;
  }
  return shared >= 2 ? 2 : shared;
}

/**
 * Room for the whole guard, not just for its first stack.
 *
 * `hasFreeSlotFor` answers for one creature against the army as it stands, and
 * two answers can be true of the same single free slot. A wandering monster is
 * one stack so the distinction never bites there, but the count is what the
 * question actually means and it costs a loop.
 */
function armyHasRoomFor(own: ArmyState | null, guard: ArmyState): boolean {
  if (own === null) return false;
  let free = 0;
  for (const s of own.slots) if (s === null) free += 1;
  const newcomers: CreatureDefId[] = [];
  for (const g of guard.slots) {
    if (g === null || g.count <= 0) continue;
    if (!hasFreeSlotFor(own, g.def)) return false;
    if (newcomers.includes(g.def)) continue;
    let held = false;
    for (const s of own.slots) if (s !== null && s.def === g.def) held = true;
    if (!held) newcomers.push(g.def);
  }
  return newcomers.length <= free;
}

/**
 * What the stack asks for. The author's per-head price is the rule; a def that
 * never got one is priced off the creature's own gold cost, which is the same
 * valuation surrender uses, so the two negotiating tables cannot disagree.
 */
function joinPrice(c: Ctx, guard: ArmyState, pay: Payload): number {
  const perHead = payNum(pay, "joinCostPerHead", 0);
  if (perHead <= 0) return armyGoldCost(c.content, guard);
  let heads = 0;
  for (const s of guard.slots) if (s !== null && s.count > 0) heads += s.count;
  return perHead * heads;
}

// ------------------------------------------------------------------ the rule

export function diplomacyOffer(
  c: Ctx, hero: HeroState, guard: ArmyId, pay: Payload,
): DiplomacyOffer {
  const stack = c.s.armies[guard] ?? null;
  if (stack === null) return FIGHT;
  const stackValue = armyValue(c.content, stack);
  // Nothing left to bargain with, and nothing to divide by either.
  if (stackValue <= 0) return FIGHT;

  const own = c.s.armies[hero.army] ?? null;
  const ratioBp = Math.floor((armyValue(c.content, own) * BP_ONE) / stackValue);
  const level = skillLevelOf(c, hero, CODED_SKILLS.diplomacy);
  const kin = sympathy(c, own, stack);
  const margin = powerFactor(ratioBp) + kin - WANDERING_DISPOSITION;
  const statBp = statFor(c, hero, "diplomacy", 0).value;

  // Below zero even with the skill counted, the classic stops asking questions: the
  // stack is not impressed enough to treat and not frightened enough to run.
  if (margin + level >= 0 && payNum(pay, "joinCostPerHead", 0) >= 0
      && armyHasRoomFor(own, stack)) {
    const willing = clampInt(
      JOIN_BASE_BP + margin * JOIN_PER_MARGIN_BP + (DIPLOMACY_JOIN_BP[level] ?? 0), 0, BP_ONE);
    if (rollChanceBp(c, "map.encounter", clampInt(willing + statBp, 0, BP_ONE))) {
      // Diplomacy is what turns a price into a gift: one level of it, or a
      // single stack of its own kind already under the hero's banner, is enough
      // for an aggressive stack to come along for nothing.
      const free = level + kin + 1 >= WANDERING_DISPOSITION;
      return { kind: "join", price: free ? 0 : joinPrice(c, stack, pay) };
    }
  }

  if (payFlag(pay, "flees") && ratioBp >= FLEE_MIN_RATIO_BP) {
    const scared = clampInt(
      FLEE_BASE_BP + margin * FLEE_PER_MARGIN_BP + (DIPLOMACY_FLEE_BP[level] ?? 0), 0, BP_ONE);
    const fear = statBp < 0 ? -statBp : 0;
    if (rollChanceBp(c, "map.encounter", clampInt(scared + fear, 0, BP_ONE))) return FLEE;
  }

  return FIGHT;
}
